import { staffPaymentListSchema, staffReconcileOutcomeSchema, staffRefundResultSchema, } from "@lafayette/schemas";
import { createMemoryTransport, } from "./memory-transport";
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const recordList = (value) => Array.isArray(value) && value.every(isRecord) ? value : null;
const evidenceOf = (value) => value === "FAKE" || value === "REAL" || value === "NONE" ? value : "NONE";
/** Tolerant on purpose: a section a server version does not send reads as empty, so an older or newer API never half-breaks the page. */
const detailParser = {
    safeParse(input) {
        if (!isRecord(input) || !isRecord(input.request))
            return { success: false };
        const quote = input.quote === undefined || input.quote === null ? null : input.quote;
        if (quote !== null && !isRecord(quote))
            return { success: false };
        const lists = {
            refunds: recordList(input.refunds ?? []),
            observations: recordList(input.observations ?? []),
            exceptions: recordList(input.exceptions ?? []),
            transitions: recordList(input.transitions ?? []),
            providerCalls: recordList(input.providerCalls ?? []),
            reconciliationRuns: recordList(input.reconciliationRuns ?? []),
        };
        const amountSource = input.amountSource === undefined || input.amountSource === null ? null : input.amountSource;
        if (Object.values(lists).some((list) => list === null))
            return { success: false };
        if (amountSource !== null && !isRecord(amountSource))
            return { success: false };
        return {
            success: true,
            data: {
                request: input.request,
                quote,
                refunds: lists.refunds ?? [],
                observations: lists.observations ?? [],
                exceptions: lists.exceptions ?? [],
                transitions: lists.transitions ?? [],
                providerCalls: lists.providerCalls ?? [],
                amountSource,
                reconciliationRuns: lists.reconciliationRuns ?? [],
                evidence: evidenceOf(input.evidence),
            },
        };
    },
};
/** `202 payment.refund_pending` is its own shape; every other answer is the contract's `staffRefundResultSchema`. */
const refundParser = {
    safeParse(input) {
        if (isRecord(input) &&
            input.code === "payment.refund_pending" &&
            input.outcome === undefined &&
            typeof input.refundId === "string") {
            return { success: true, data: { kind: "PENDING", refundId: input.refundId } };
        }
        const parsed = staffRefundResultSchema.safeParse(input);
        return parsed.success
            ? { success: true, data: { kind: "RESULT", ...parsed.data } }
            : { success: false };
    },
};
/** The path of one staff payment request. The id is encoded, so it can never add a path segment. */
export function staffPaymentPath(paymentRequestId, action) {
    const base = `/staff/payment-requests/${encodeURIComponent(paymentRequestId)}`;
    return action === undefined ? base : `${base}/${action}`;
}
export function createStaffPaymentsApi(options) {
    const send = createMemoryTransport(options);
    return {
        list(query, callOptions) {
            const params = new URLSearchParams();
            if (query?.filter !== undefined)
                params.set("filter", query.filter);
            if (query?.cursor !== undefined)
                params.set("cursor", query.cursor);
            if (query?.limit !== undefined)
                params.set("limit", String(query.limit));
            return send({
                method: "GET",
                path: "/staff/payment-requests",
                query: params,
                schema: staffPaymentListSchema,
                signal: callOptions?.signal,
            });
        },
        get: (paymentRequestId, callOptions) => send({
            method: "GET",
            path: staffPaymentPath(paymentRequestId),
            schema: detailParser,
            signal: callOptions?.signal,
        }),
        reconcile: (paymentRequestId, o) => send({
            method: "POST",
            path: staffPaymentPath(paymentRequestId, "reconcile"),
            body: {},
            schema: staffReconcileOutcomeSchema,
            idempotencyKey: o?.idempotencyKey,
            signal: o?.signal,
        }),
        refund: (paymentRequestId, input, o) => send({
            method: "POST",
            path: staffPaymentPath(paymentRequestId, "refunds"),
            body: input,
            schema: refundParser,
            idempotencyKey: o.idempotencyKey,
            signal: o.signal,
        }),
    };
}
